import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

/** Product shown on a footer cart card in preview scenarios. */
export type KioskFooterCartPreviewProduct = {
  id: string;
  name: string;
  images?: {
    thumbnail?: { url: string };
  };
};

/** One line of the horizontal product strip in the kiosk footer. */
export type KioskFooterCartPreviewCartItem = {
  item: {
    id: number;
    item_id: string;
    quantity: number;
    additions: string[];
  };
  product: KioskFooterCartPreviewProduct;
};

/** Serializable reconstructed cart group (one shop + its line items). */
export type KioskFooterCartPreviewCart = {
  shop: {
    id: string;
    name: string;
  };
  items: readonly KioskFooterCartPreviewCartItem[];
};

/**
 * Serializable preview content for `KioskFooterCartBlockComponent`.
 * `finalPrice` is in cents, matching order validation.
 */
export type KioskFooterCartBlockScenarioContent = {
  reconstructedCart: readonly KioskFooterCartPreviewCart[];
  reconstructedShareOrderBasket: readonly unknown[];
  finalPrice: number;
};

export const KIOSKFOOTERCARTBLOCK_LEGACY_FIELD_ORDER = [
  "background_color",
  "top_border_color",
  { type: "separator" },
  "primary_button_background_color",
  "primary_button_text_color",
  { type: "separator" },
  "secondary_button_background_color",
  "secondary_button_text_color",
  { type: "separator" },
  "product_background_color",
  "product_text_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskFooterCartBlockDescriptor: _generatedKioskFooterCartBlockDescriptor,
  kioskFooterCartBlockBlocDefinition,
  kioskFooterCartBlockDashboardLayout,
  kioskFooterCartBlockConfig,
  kioskFooterCartBlockResolveConfig,
  kioskFooterCartBlockProperties,
  kioskFooterCartBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Kiosk Footer Cart",
    componentType: "KioskFooterCartBlockComponent",
    category: "kiosk",
    description: "Kiosk Footer Cart.",
    keywords: [],
    allowedIn: [
      { bloc: 'KioskPromoSelectProductPageComponent', slot: 'footer' },
    ],


    sharedFields: {},
  },

  () => ({
    background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-50" },
      options: {
        label: "pages.blocks.kiosk-footer-cart.background-color.label",
      },
    }),
    top_border_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.kiosk-footer-cart.top-border-color.label",
        help: "pages.blocks.kiosk-footer-cart.top-border-color.help",
      },
    }),
    primary_button_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label:
          "pages.blocks.kiosk-footer-cart.primary-button-background-color.label",
      },
    }),
    primary_button_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-50" },
      options: {
        label: "pages.blocks.kiosk-footer-cart.primary-button-text-color.label",
      },
    }),
    secondary_button_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-50" },
      options: {
        label:
          "pages.blocks.kiosk-footer-cart.secondary-button-background-color.label",
      },
    }),
    secondary_button_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label:
          "pages.blocks.kiosk-footer-cart.secondary-button-text-color.label",
      },
    }),
    product_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-50" },
      options: {
        label: "pages.blocks.kiosk-footer-cart.product-background-color.label",
      },
    }),
    product_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.kiosk-footer-cart.product-text-color.label",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.kiosk-footer-cart.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.kiosk-footer-cart.fieldset.footer.label",
            fields: ["background_color", "top_border_color"],
          },
          {
            legend:
              "pages.blocks.kiosk-footer-cart.fieldset.primary-button.label",
            fields: [
              "primary_button_background_color",
              "primary_button_text_color",
            ],
          },
          {
            legend:
              "pages.blocks.kiosk-footer-cart.fieldset.secondary-button.label",
            fields: [
              "secondary_button_background_color",
              "secondary_button_text_color",
            ],
          },
          {
            legend: "pages.blocks.kiosk-footer-cart.fieldset.product.label",
            fields: ["product_background_color", "product_text_color"],
          },
        ],
      },
    ],
  }),
);

export function kioskFooterCartBlockDescriptor(): IBlockDescriptor;
export function kioskFooterCartBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskFooterCartBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskFooterCartBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedKioskFooterCartBlockDescriptor,
    kioskFooterCartBlockProperties,
    fieldOrder,
  );
}

export {
  kioskFooterCartBlockBlocDefinition,
  kioskFooterCartBlockDashboardLayout,
  kioskFooterCartBlockProperties,
  kioskFooterCartBlockConfig,
  kioskFooterCartBlockResolveConfig,
  kioskFooterCartBlockDefinitionSetupScenarios,
};

export type KioskFooterCartBlockConfig = typeof kioskFooterCartBlockConfig;
